import { ExclamationCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { PageLimitFilter } from '@typings';
import {
  Col,
  Dropdown,
  MenuProps,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { MenuInfo } from 'rc-menu/lib/interface';
import React, { useCallback, useRef } from 'react';

import {
  EGateway,
  EPaymentMethod,
  ESubscriptionType,
  GATEWAY_LABEL,
  PAYMENT_METHOD_LABEL,
  SUBSCRIPTION_STATUS_COLORS,
  SUBSCRIPTION_STATUS_LABEL,
  SUBSCRIPTION_TYPE_LABEL,
} from '../../constants/financial';
import { ESubscriptionStatus } from '../../constants/financial';
import {
  useCancelAutoRescue,
  useCancelSubscription,
  useReactiveSubscription,
  useReasonOptions,
} from '../../hooks/useCustomerFinancial';
import { Subscription } from '../../types/customer';
import { dateUtils } from '../../utils/dates';
import { stringUtils } from '../../utils/strings';
import { BillingsTable } from './BillingsTable';
import { SelectReason } from './components/SelectReason';

export type SubscriptionsTableProps = {
  subscriptions: Subscription[];
  isLoading: boolean;
  customerDocument: string;
  customerEmail: string;
  pagination: PageLimitFilter & { totalResults: number };
  onPaginationChange: (page: number) => void;
};

const { confirm } = Modal;
const { Text } = Typography;
const DATE_FORMAT = 'dd/MM/yyy HH:mm:ss';

export const SubscriptionsTable: React.FC<SubscriptionsTableProps> = ({
  subscriptions,
  isLoading,
  customerDocument,
  customerEmail,
  pagination,
  onPaginationChange,
}) => {
  const { cancelSubscription } = useCancelSubscription();
  const { reactiveSubscription } = useReactiveSubscription();
  const { cancelAutoRescue } = useCancelAutoRescue();
  const reasonRef = useRef(0);
  const handleChange = (value: number) => {
    reasonRef.current = value;
  };
  const { data: options, isLoading: isLoadingReasons } = useReasonOptions();

  const showCancelRenewalModal = useCallback(
    (subscriptionId: number) => {
      confirm({
        icon: <ExclamationCircleOutlined style={{ color: '#FAAD14' }} />,
        title: (
          <Text style={{ fontWeight: 600 }}>Cancelar Renovação Automática</Text>
        ),
        content: (
          <SelectReason
            description='Você está prestes a cancelar a renovação deste usuário. Selecione um
            motivo para continuar:'
            confirmationAsk='Tem certeza que deseja prosseguir com a alteração?'
            handleChange={handleChange}
            options={options}
            loading={isLoadingReasons}
          />
        ),
        centered: true,
        width: 704,
        okText: 'Cancelar Renovação',
        okType: 'danger',
        okButtonProps: { type: 'primary' },
        cancelText: 'Cancelar',
        onOk: () => {
          cancelSubscription({ subscriptionId, reasonId: reasonRef.current });
        },
      });
    },
    [options, reasonRef, cancelSubscription]
  );

  const showReactivateRenewalModal = useCallback(
    (subscriptionId: number) => {
      confirm({
        icon: <ExclamationCircleOutlined style={{ color: '#FAAD14' }} />,
        title: (
          <Text style={{ fontWeight: 600 }}>Ativar Renovação Automática</Text>
        ),
        content: (
          <Text>
            Você está prestes a reativar a renovação automática. Tem certeza que
            deseja prosseguir com a alteração?
          </Text>
        ),
        centered: true,
        width: 650,
        okText: 'Reativar Renovação',
        cancelText: 'Cancelar',
        onOk: () => {
          reactiveSubscription(subscriptionId);
        },
      });
    },
    [reactiveSubscription]
  );

  const showCancelSubscriptionModal = useCallback(
    (subscriptionId: number) => {
      confirm({
        icon: <ExclamationCircleOutlined style={{ color: '#FAAD14' }} />,
        title: <Text style={{ fontWeight: 600 }}>Cancelar Assinatura</Text>,
        content: (
          <SelectReason
            description='
          Você está prestes a cancelar a assinatura deste usuário. Selecione um motivo para continuar:'
            confirmationAsk='Tem certeza que deseja prosseguir com a alteração?'
            options={options}
            handleChange={handleChange}
          />
        ),
        centered: true,
        width: 650,
        okText: 'Cancelar Assinatura',
        okType: 'danger',
        okButtonProps: { type: 'primary' },
        cancelText: 'Cancelar',
        onOk: () => {
          cancelAutoRescue({
            subscriptionId,
            reasonId: reasonRef.current,
          });
        },
      });
    },
    [options, reasonRef, cancelAutoRescue]
  );

  const modalMapping: Record<string, (subscriptionId: number) => void> = {
    'cancel-renewal': showCancelRenewalModal,
    'reactivate-renewal': showReactivateRenewalModal,
    'cancel-subscription': showCancelSubscriptionModal,
  };

  const handleMenuClick = (e: MenuInfo, subscriptionId: number) => {
    const action = e.key;
    const showModal = modalMapping[action];
    if (showModal) showModal(subscriptionId);
  };

  const generateMenuItems = useCallback(
    (rowData: Subscription): MenuProps['items'] => [
      {
        label: 'Cancelar Renovação',
        key: 'cancel-renewal',
        style: {
          color: rowData.autoRenew ? 'rgba(22, 104, 220, 1)' : undefined,
        },
        disabled: !rowData.autoRenew,
      },
      {
        type: 'divider',
      },
      {
        label: 'Reativar Renovação',
        key: 'reactivate-renewal',
        style: {
          color: !rowData.autoRenew ? 'rgba(22, 104, 220, 1)' : undefined,
        },
        disabled: !!rowData.autoRenew,
      },
      {
        type: 'divider',
      },
      {
        label: 'Cancelar Assinatura',
        key: 'cancel-subscription',
        danger: true,
      },
    ],
    []
  );

  const subscriptionColumns: ColumnsType<Subscription> = [
    {
      title: 'ID Assinatura',
      dataIndex: 'id',
      key: 'id',
      render: id => <Row style={{ width: '200px' }}>{id}</Row>,
    },
    {
      title: 'ID Produto',
      dataIndex: 'productSellGroupId',
      key: 'productSellGroupId',
      render: productSellGroupId => (
        <Row style={{ width: '200px' }}>{productSellGroupId}</Row>
      ),
    },
    {
      title: 'Nome do Produto',
      dataIndex: 'productSellGroupName',
      key: 'productSellGroupName',
      render: productSellGroupName => (
        <Row style={{ width: '200px' }}>{productSellGroupName}</Row>
      ),
    },
    {
      title: 'ID Oferta',
      dataIndex: 'offerId',
      key: 'offerId',
      render: offerId => <Row style={{ width: '200px' }}>{offerId}</Row>,
    },
    {
      title: 'Nome Oferta',
      dataIndex: 'offerName',
      key: 'offerName',
      render: offerName => <Row style={{ width: '200px' }}>{offerName}</Row>,
    },
    {
      title: 'Valor',
      dataIndex: 'offerAmount',
      key: 'offerAmount',
      render: offerAmount => (
        <Row style={{ width: '200px' }}>
          {stringUtils.formatToReal(offerAmount)}
        </Row>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: ESubscriptionStatus) => (
        <Row style={{ width: '200px' }}>
          <Tag color={SUBSCRIPTION_STATUS_COLORS[status]}>
            {SUBSCRIPTION_STATUS_LABEL[status]}
          </Tag>
        </Row>
      ),
    },
    {
      title: 'Tipo Cobrança',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      render: (subscriptionType: ESubscriptionType) => (
        <Row style={{ width: '200px' }}>
          {SUBSCRIPTION_TYPE_LABEL[subscriptionType]}
        </Row>
      ),
    },
    {
      title: 'Método Pagamento',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (paymentMethod: EPaymentMethod) => (
        <Row style={{ width: '200px' }}>
          {PAYMENT_METHOD_LABEL[paymentMethod]}
        </Row>
      ),
    },
    {
      title: 'Gateway',
      dataIndex: 'gateway',
      key: 'gateway',
      render: (gateway: EGateway) => (
        <Row style={{ width: '200px' }}>{GATEWAY_LABEL[gateway]}</Row>
      ),
    },
    {
      title: 'Auto Renovação',
      dataIndex: 'autoRenew',
      key: 'autoRenew',
      render: (autoRenew: boolean) => (
        <Row style={{ width: '200px' }}>{autoRenew ? 'Sim' : 'Não'}</Row>
      ),
    },
    {
      title: 'Data Início',
      dataIndex: 'startDate',
      key: 'startDate',
      render: startDate => (
        <Row style={{ width: '200px' }}>
          {dateUtils.formatDateString(startDate, DATE_FORMAT)}
        </Row>
      ),
    },
    {
      title: 'Data Fim',
      dataIndex: 'endDate',
      key: 'endDate',
      render: endDate => (
        <Row style={{ width: '200px' }}>
          {dateUtils.formatDateString(endDate, DATE_FORMAT)}
        </Row>
      ),
    },
    {
      title: 'Data de Garantia',
      dataIndex: 'warrantyDate',
      key: 'warrantyDate',
      render: warrantyDate => (
        <Row style={{ width: '200px' }}>
          {dateUtils.formatDateString(warrantyDate, DATE_FORMAT)}
        </Row>
      ),
    },
    {
      title: 'Qtdade Recorrência',
      dataIndex: 'subscriptionRecurrency',
      key: 'subscriptionRecurrency',
      render: recurrenceCount => (
        <Row style={{ width: '200px' }}>{recurrenceCount}</Row>
      ),
    },
    {
      title: 'Período',
      dataIndex: 'subscriptionPeriod',
      key: 'subscriptionPeriod',
      render: subscriptionPeriod => (
        <Row style={{ width: '200px' }}>{subscriptionPeriod}</Row>
      ),
    },
    {
      title: 'Data Atualização',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: updatedAt => (
        <Row style={{ width: '200px' }}>
          {dateUtils.formatDateString(updatedAt, DATE_FORMAT)}
        </Row>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <Row justify={'center'} align={'middle'} style={{ cursor: 'pointer' }}>
          <Dropdown
            menu={{
              items: generateMenuItems(record),
              onClick: e => handleMenuClick(e, record.id),
            }}
            placement='bottomRight'
            trigger={['click']}
          >
            <Col>
              <Space>
                <MoreOutlined />
              </Space>
            </Col>
          </Dropdown>
        </Row>
      ),
    },
  ];

  return (
    <Table
      columns={subscriptionColumns}
      dataSource={subscriptions}
      rowKey='id'
      pagination={{
        pageSize: pagination.limit,
        current: pagination.page,
        total: pagination.totalResults,
        position: ['bottomLeft'],
      }}
      onChange={paginate => {
        if (paginate.current) {
          onPaginationChange(paginate.current);
        }
      }}
      expandable={{
        expandedRowRender: subscription => (
          <BillingsTable
            subscriptionId={subscription.id}
            customerDocument={customerDocument}
            customerEmail={customerEmail}
          />
        ),
      }}
      loading={isLoading}
    />
  );
};
